import {Component, computed, effect, inject, signal, viewChild} from '@angular/core';
import {FormControl, FormGroup} from '@angular/forms';
import {TranslatePipe} from '@ngx-translate/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {debounceTime} from 'rxjs';
import {TaxSummary} from '../../../../../../../api';
import {TaxQueryService} from './services/tax-query.service';
import {TaxCommandService} from './services/tax-command.service';
import {ElciaButton} from '../../../../../../components/buttons/button/button';
import {ConfirmService} from '../../../../../../services/confirm.service';
import {ConfirmDialog} from '../../../../../../components/confirm-dialog/confirm-dialog';
import {ElciaDialog} from '../../../../../../components/dialog/dialog';
import {Search} from '../../../../../../components/search/search';
import {AccountingTaxGrid} from './components/accounting-tax-grid/accounting-tax-grid';
import {AccountingTaxForm, TaxFormValue} from './components/accounting-tax-form/accounting-tax-form';

@Component({
  selector: 'elcia-accounting-settings-taxes',
  imports: [TranslatePipe, ElciaButton, ConfirmDialog, ElciaDialog, AccountingTaxGrid, AccountingTaxForm, Search],
  providers: [TaxQueryService, TaxCommandService],
  templateUrl: './accounting-settings-taxes.html',
  styleUrl: './accounting-settings-taxes.scss',
})
export class AccountingSettingsTaxes {
  private readonly queryService = inject(TaxQueryService);
  private readonly commandService = inject(TaxCommandService);
  private readonly confirmService = inject(ConfirmService);

  private readonly taxForm = viewChild<AccountingTaxForm>(AccountingTaxForm);

  protected readonly isLoading = computed(() => this.queryService.isLoading());
  protected readonly taxes = computed(() => this.queryService.taxes());

  protected readonly isFormDisplayed = signal(false);
  private readonly openMode = signal<'create' | 'edit'>('create');
  protected readonly selectedTax = signal<TaxSummary | null>(null);

  protected readonly filterForm = new FormGroup({
    searchTerm: new FormControl('', {nonNullable: true}),
  });

  private readonly filterValue = toSignal(
    this.filterForm.valueChanges.pipe(debounceTime(300)),
    {initialValue: this.filterForm.value}
  );

  private readonly syncFilter = effect(() => {
    const form = this.filterValue();
    this.queryService.filterParams.set({searchTerm: form.searchTerm || null});
  });

  protected readonly isEditMode = computed(() => this.openMode() === 'edit');
  protected readonly isSaving = computed(() =>
    this.commandService.createMutation.isPending() || this.commandService.updateMutation.isPending()
  );

  protected showForm(): void {
    this.selectedTax.set(null);
    this.openMode.set('create');
    this.isFormDisplayed.set(true);
  }

  protected openEditForm(tax: TaxSummary): void {
    this.selectedTax.set(tax);
    this.openMode.set('edit');
    this.isFormDisplayed.set(true);
  }

  protected closeForm(): void {
    this.isFormDisplayed.set(false);
    this.selectedTax.set(null);
  }

  protected onDialogSave(): void {
    this.taxForm()?.submit();
  }

  protected onFormSubmit(value: TaxFormValue): void {
    if (this.openMode() === 'edit' && value.id != null) {
      this.commandService.updateMutation.mutate({
        id: value.id,
        body: {
          nameFr: value.nameFr!,
          nameEn: value.nameEn!,
          nameNl: value.nameNl!,
          rate: value.rate!,
          peppolCode: value.peppolCode!,
          isDefault: value.isDefault ?? false,
          deductibleVatPercentage: value.deductibleVatPercentage!,
          accountingCode: value.accountingCode ?? '',
          accountingAccountId: value.accountingAccountId ?? 0,
          accountingCode2: value.accountingCode2,
          accountingAccount2Id: value.accountingAccount2Id,
        },
      });
    } else {
      this.commandService.createMutation.mutate({
        nameFr: value.nameFr!,
        nameEn: value.nameEn!,
        nameNl: value.nameNl!,
        rate: value.rate!,
        peppolCode: value.peppolCode!,
        isDefault: value.isDefault ?? false,
        deductibleVatPercentage: value.deductibleVatPercentage!,
        accountingCode: value.accountingCode,
        accountingAccountId: value.accountingAccountId,
        accountingCode2: value.accountingCode2,
        accountingAccount2Id: value.accountingAccount2Id,
      });
    }
  }

  protected deleteTax(tax: TaxSummary): void {
    if (!tax.id) return;
    this.confirmService.confirm({
      title: 'entity.tax',
      message: `${tax.nameFr}/${tax.nameEn}/${tax.nameNl}`,
      accept: () => {
        this.commandService.deleteMutation.mutate(tax.id!);
      },
    });
  }

  private readonly closeFormOnSuccess = effect(() => {
    const createSuccess = this.commandService.createMutation.isSuccess();
    const updateSuccess = this.commandService.updateMutation.isSuccess();
    if (createSuccess || updateSuccess) {
      this.closeForm();
    }
  });
}
